"use client";

import { useRef, useState, useCallback } from "react";
import { gsap, ScrollTrigger, useGSAP } from "@/lib/gsap";

interface MetricCounterProps {
  /** The final numeric value to count to. */
  value: number;
  /** Decimals to render. Default: 0. */
  decimals?: number;
  /** Animation duration in ms. Default: 1600. */
  duration?: number;
  /** Optional prefix (e.g., "$"). */
  prefix?: string;
  /** Optional suffix (e.g., "ms", "%"). */
  suffix?: string;
  /** Locale for number formatting. Default: "en-US". */
  locale?: string;
  /** Use compact notation (e.g., "1.2M"). Default: false. */
  compact?: boolean;
  /** className applied to the rendered span. */
  className?: string;
}

/**
 * GSAP-powered number counter. Tweens from 0 → value when scrolled into view.
 * Uses GSAP's easing for buttery smooth counting that syncs with ScrollTrigger.
 * Respects reduced motion (renders final value immediately).
 */
export default function MetricCounter({
  value,
  decimals = 0,
  duration = 1600,
  prefix = "",
  suffix = "",
  locale = "en-US",
  compact = false,
  className = "",
}: MetricCounterProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = useState(0);
  const proxyRef = useRef({ val: 0 });

  const formatter = useCallback(
    (n: number) => {
      const fmt = new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        notation: compact ? "compact" : "standard",
      });
      return fmt.format(n);
    },
    [locale, decimals, compact],
  );

  useGSAP(
    () => {
      if (!ref.current) return;

      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        proxyRef.current.val = 0;

        ScrollTrigger.create({
          trigger: ref.current,
          start: "top 85%",
          once: true,
          onEnter: () => {
            gsap.to(proxyRef.current, {
              val: value,
              duration: duration / 1000,
              ease: "power3.out",
              onUpdate: () => {
                setDisplay(proxyRef.current.val);
              },
            });
          },
        });
      });

      mm.add("(prefers-reduced-motion: reduce)", () => {
        setDisplay(value);
      });
    },
    { scope: ref },
  );

  return (
    <span ref={ref} className={`tabular-nums ${className}`}>
      {prefix}
      {formatter(display)}
      {suffix}
    </span>
  );
}
